import { QuotePlatformSourceEnum } from '@jove-frontend/enums';
import { QuoteDetailFormDataProps } from '@jove-frontend/types';
import { PROVIDER_SOURCE, apiGetUsers, apiQuoteGetAdminQuotePrice } from '@jove-frontend/uikit';
import axios from 'axios';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

export interface QuoteDetailExtraRelatedInfoProps {
  tripartiteInfo?: {
    id: string;
    slug: string;
    parent_id: string;
    sub_broker_type: string;
  };
  platformSourceType?: QuotePlatformSourceEnum;
}

interface QuoteDetailStoreState {
  isLoading: boolean;
  autoSaveLoading: boolean;
  fieldChangeWatchMap: {
    businessActivities: string;
    businessCountry: string;
    turnover: string;
  };
  assigneeList: Array<{ value: string; label: string }>;
  calculatePriceLoading: boolean;
  quotePriceInfo: any;
  price: any;
  coverOptions: any;
  quotePriceInfoCancelToken: any;
  extraRelatedInfo: QuoteDetailExtraRelatedInfoProps;
}

export interface QuoteDetailStoreActions {
  handleLoading: (show: boolean) => void;
  handleAutoSaveLoading: (show: boolean) => void;
  fieldEmitChange: (filedName: string) => void;
  updateQuoteDetailAssigneeList: () => void;
  updateQuoteDetailPriceInfo: (quoteInfo: { quoteId: string }, quoteData?: QuoteDetailFormDataProps) => void;
  updatePriceInfo: (info: any) => void;
  cleanQuoteDetailPriceInfo: () => void;
  updateQuoteExtraRelatedInfo: (info: QuoteDetailExtraRelatedInfoProps) => void;
}

interface QuoteDetailStore {
  state: QuoteDetailStoreState;
  actions: QuoteDetailStoreActions;
}

export const useQuoteDetailStore = create<QuoteDetailStore>()(
  immer((set, get) => ({
    state: {
      isLoading: false,
      autoSaveLoading: false,
      fieldChangeWatchMap: {
        businessActivities: '1',
        turnover: '1',
        businessCountry: '1',
      },
      assigneeList: [],
      calculatePriceLoading: false,
      quotePriceInfo: {},
      price: null,
      coverOptions: null,
      quotePriceInfoCancelToken: null,
      extraRelatedInfo: null,
    },
    actions: {
      handleLoading: (show = false) => {
        set((store) => {
          store.state.isLoading = show;
        });
      },
      handleAutoSaveLoading: (show = false) => {
        set((store) => {
          store.state.autoSaveLoading = show;
        });
      },
      fieldEmitChange: (filedName) => {
        if (get().state.fieldChangeWatchMap[filedName]) {
          set((store) => {
            store.state.fieldChangeWatchMap[filedName] = String(parseInt(get().state.fieldChangeWatchMap[filedName]) + 1);
          });
        }
      },
      updateQuoteDetailAssigneeList: async () => {
        const params = {
          organization_slug: 'joveco',
        };
        const [res] = await apiGetUsers(params);
        if (res) {
          const list = (res.data || []).map((item) => {
            return {
              value: item.user.id,
              label: `${item.user.first_name} ${item.user.last_name}`,
            };
          });
          list.unshift({ value: '', label: 'Unassigned' });
          set((store) => {
            store.state.assigneeList = list;
          });
        }
      },
      updateQuoteDetailPriceInfo: async (params, quoteData) => {
        const { quotePriceInfoCancelToken } = get().state;
        if (quotePriceInfoCancelToken) {
          quotePriceInfoCancelToken?.cancel();
        }

        set((store) => {
          store.state.calculatePriceLoading = true;
        });

        const axiosCancelToken = axios.CancelToken.source();
        const [res] = await apiQuoteGetAdminQuotePrice(params, {
          notRequestCoverOptions: [PROVIDER_SOURCE().CHUBB.value].includes(quoteData?.providerCode),
          cancelToken: axiosCancelToken.token,
        });
        set((store) => {
          store.state.quotePriceInfo = res?.priceDetailInfo || {};
          store.state.coverOptions = res?.coverOptions || {};
          store.state.calculatePriceLoading = false;
          store.state.quotePriceInfoCancelToken = axiosCancelToken;
        });
      },
      // Update price info: For old greate american, to be delete.
      updatePriceInfo: (info) => {
        set((store) => {
          store.state.price = info;
        });
      },
      updateQuoteExtraRelatedInfo: (info) => {
        set((store) => {
          store.state.extraRelatedInfo = {
            tripartiteInfo: info?.tripartiteInfo || null,
            platformSourceType: info?.platformSourceType || null,
          };
        });
      },
      cleanQuoteDetailPriceInfo: () => {
        set((store) => {
          store.state.quotePriceInfo = {};
        });
      },
    },
  }))
);
